import { CopyButton } from '@/components/copy-button';
import { Tooltip } from '@/components/tooltip';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/tailwind-utils';
import clsx from 'clsx';
import { ExternalLink } from 'lucide-react';
import { ComponentProps, type MouseEventHandler, ReactNode } from 'react';

type ButtonPropsWithTestId = ComponentProps<typeof Button> & { 'data-testid'?: string };

export const StartOrJoinSession = ({
  label,
  url,
  whenUrlEmptyRender,
  canCopyUrl,
  startButtonProps = {},
  disabledTooltip,
}: {
  label: string;
  url?: string | null;
  whenUrlEmptyRender?: ReactNode;
  startButtonProps?: ButtonPropsWithTestId;
  canCopyUrl?: boolean;
  disabledTooltip?: string;
}) => {
  if (!whenUrlEmptyRender) {
    whenUrlEmptyRender = <Button disabled>{label}</Button>;
  }

  const {
    className,
    onClick,
    size,
    variant,
    icon,
    iconClassName = '',
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    ...restButtonProps
  } = startButtonProps;
  const Icon = 'icon' in startButtonProps ? icon : ExternalLink;

  if (url && disabledTooltip) {
    return (
      <Tooltip
        trigger={
          <span className="inline-block cursor-not-allowed">
            <Button
              icon={Icon}
              className={className}
              size={size}
              variant={variant}
              data-testid={dataTestId}
              aria-label={ariaLabel}
              {...restButtonProps}
              type="button"
              disabled
            >
              {label}
            </Button>
          </span>
        }
      >
        {disabledTooltip}
      </Tooltip>
    );
  }

  return url ? (
    <div className="flex gap-2 min-w-0 w-full">
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className={cn(buttonVariants({ variant, size, className }), 'min-w-0 flex-1')}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement> | undefined}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        {Icon ? <Icon className={clsx('h-5 w-5', label && 'mr-2', iconClassName)} /> : null}
        {label}
      </a>
      {canCopyUrl && <CopyButton content={url} />}
    </div>
  ) : (
    whenUrlEmptyRender
  );
};
